import { Divider } from "@msuite/ui/divider";
import { Combobox, Select, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import type { ContactFormSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useContacts } from "@/hooks/use-contacts";
import { useTableTypes } from "@/hooks/use-table-types";
import { AddressForm } from "../address-form";

export function ContactForm() {
	/** Context */
	const methods = useFormContext<ContactFormSchema>();

	/** Hooks */
	const { t } = useTranslation();
	const { data: contactTypes } = useTableTypes("contactTypes", ["active"]);
	const { contacts } = useContacts();

	/** Render */
	return (
		<React.Fragment>
			<Select
				control={methods.control}
				name="contactTypeId"
				label={t("general.type")}
				isRequired
				isNullable={false}
				itemNotFoundLabel={t("general.select")}
				options={
					contactTypes?.tableTypes.map((type) => ({
						label: type.name,
						value: type.id,
						item: type,
					})) || []
				}
				renderOption={(item) => (
					<div className="h-stack">
						<StatusIndicator color={item.color} />
						<p>{item.name}</p>
					</div>
				)}
				data-cy="contact-type-select"
			/>
			<TextInput
				autoFocus
				label={t("contacts.fields.name")}
				control={methods.control}
				name="name"
				placeholder={t("placeholders.company_name")}
				isRequired
				data-cy="contact-name-input"
			/>
			<AddressForm />
			<Divider variant="modal" />
			<TextInput
				label={t("personnel.fields.email")}
				control={methods.control}
				name="email"
				placeholder={t("placeholders.email")}
				isRequired={false}
				data-cy="contact-email-input"
			/>
			<TextInput
				label={t("personnel.fields.phone")}
				control={methods.control}
				name="phone"
				placeholder={t("placeholders.phone")}
				isRequired={false}
				data-cy="contact-phone-input"
			/>
			<Divider variant="modal" />
			<TextInput
				label={t("contacts.vat_id")}
				control={methods.control}
				name="vatId"
				placeholder="DE123456789"
				isRequired={false}
				data-cy="contact-vat-id-input"
			/>
			<TextInput
				label={t("contacts.tax_number")}
				control={methods.control}
				name="taxNumber"
				placeholder="12/345/67890"
				isRequired={false}
				data-cy="contact-tax-number-input"
			/>
			<Divider variant="modal" />
			<Combobox
				control={methods.control}
				name="relatedContactId"
				options={contacts}
				displayValue={(item) => item.name}
				label={t("contacts.related_contact")}
				keyExtractor={(item) => item.id}
				placeholder={t("placeholders.company_name")}
				isRequired={false}
				data-cy="contact-related-contact-picker"
			/>
		</React.Fragment>
	);
}
